import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff, Shield, Clock, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface ApiToken {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

interface ScopeOption {
  value: string;
  label: string;
  hint: string;
  /** Ticked when the modal opens, so a fresh key works with n8n and MCP out of the box. */
  defaultOn: boolean;
}

const AVAILABLE_SCOPES: ScopeOption[] = [
  { value: 'accounts:read', label: 'Odczyt kont', hint: 'Lista podłączonych kont i ich identyfikatorów', defaultOn: true },
  { value: 'facebook:publish', label: 'Publikacja Facebook', hint: 'Posty, wideo i komentarze na Facebooku. Działa też jako alias dla pozostałych sieci (zgodność wsteczna)', defaultOn: true },
  { value: 'facebook:read', label: 'Odczyt Facebook', hint: 'Posty, komentarze i statystyki', defaultOn: true },
  { value: 'youtube:publish', label: 'Publikacja YouTube', hint: 'Wideo i Shorty, miniatury, playlisty', defaultOn: true },
  { value: 'instagram:publish', label: 'Publikacja Instagram', hint: 'Reels i Stories', defaultOn: true },
  { value: 'linkedin:publish', label: 'Publikacja LinkedIn', hint: 'Wideo i posty', defaultOn: true },
  { value: 'pinterest:publish', label: 'Publikacja Pinterest', hint: 'Piny i tablice. Osobne uprawnienie - facebook:publish go NIE zastępuje, klucz bez niego dostaje odmowę z nazwą brakującego zakresu', defaultOn: true },
  { value: 'media:write', label: 'Wgrywanie plików', hint: 'Podpisane URL-e do wysyłki dużych wideo prosto do Storage', defaultOn: true },
  { value: 'mcp:use', label: 'Most MCP', hint: 'Dostęp przez Claude Code, Claude Desktop, Cursor i inne klienty MCP', defaultOn: true },
  { value: 'subscription:read', label: 'Odczyt subskrypcji', hint: 'Stan planu i limitów', defaultOn: false },
];

const DEFAULT_SCOPES = AVAILABLE_SCOPES.filter(s => s.defaultOn).map(s => s.value);

const MCP_ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mcp`;

const mcpAddCommand = (token: string) =>
  `claude mcp add --transport http aiagenthub ${MCP_ENDPOINT} --header "Authorization: Bearer ${token}"`;

export function UserApiTokens() {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<string[]>(DEFAULT_SCOPES);
  const [createdScopes, setCreatedScopes] = useState<string[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmToken, setDeleteConfirmToken] = useState<ApiToken | null>(null);

  useEffect(() => {
    loadTokens();
  }, []);

  // A modal is rendered into document.body, so the page behind it stays
  // scrollable unless we say otherwise — and scrolling it under an open dialog
  // is what made the overlap visible in the first place.
  useEffect(() => {
    if (!showCreateModal && !deleteConfirmToken) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [showCreateModal, deleteConfirmToken]);

  const loadTokens = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_api_tokens')
        .select('id, name, token_prefix, scopes, is_active, last_used_at, expires_at, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTokens(data || []);
    } catch (error) {
      console.error('Error loading API tokens:', error);
      toast.error('Nie udało się pobrać kluczy API');
    } finally {
      setLoading(false);
    }
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  const handleCreate = async () => {
    if (!newTokenName.trim() || newTokenName.trim().length < 2) {
      toast.error('Nazwa klucza musi mieć min. 2 znaki');
      return;
    }
    if (selectedScopes.length === 0) {
      toast.error('Zaznacz przynajmniej jedno uprawnienie');
      return;
    }

    setCreating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api-token-generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ name: newTokenName.trim(), scopes: selectedScopes }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create token');
      }

      const data = await response.json();
      setCreatedToken(data.token);
      setCreatedScopes(data.scopes || selectedScopes);
      toast.success('Klucz API wygenerowany pomyślnie!');
      await loadTokens();
    } catch (error) {
      console.error('Error creating token:', error);
      toast.error(error instanceof Error ? error.message : 'Nie udało się utworzyć klucza');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (tokenId: string) => {
    setDeletingId(tokenId);
    try {
      const { error } = await supabase
        .from('user_api_tokens')
        .delete()
        .eq('id', tokenId);

      if (error) throw error;
      setTokens(tokens.filter(t => t.id !== tokenId));
      toast.success('Klucz API usunięty');
    } catch (error) {
      console.error('Error deleting token:', error);
      toast.error('Nie udało się usunąć klucza');
    } finally {
      setDeletingId(null);
      setDeleteConfirmToken(null);
    }
  };

  const toggleActive = async (tokenId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('user_api_tokens')
        .update({ is_active: !isActive, updated_at: new Date().toISOString() })
        .eq('id', tokenId);

      if (error) throw error;
      setTokens(tokens.map(t => t.id === tokenId ? { ...t, is_active: !isActive } : t));
      toast.success(isActive ? 'Klucz dezaktywowany' : 'Klucz aktywowany');
    } catch (error) {
      console.error('Error toggling token:', error);
      toast.error('Nie udało się zmienić statusu klucza');
    }
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Skopiowano do schowka');
      setTimeout(() => setCopiedKey(current => (current === key ? null : current)), 3000);
    } catch {
      toast.error('Nie udało się skopiować');
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Key className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-xl font-semibold">Klucze API</h2>
            <p className="text-sm text-gray-500">Klucze do integracji z n8n i innymi narzędziami</p>
          </div>
        </div>
        <button
          onClick={() => {
            setNewTokenName('');
            setCreatedToken(null);
            setCreatedScopes([]);
            setSelectedScopes(DEFAULT_SCOPES);
            setCopiedKey(null);
            setShowCreateModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nowy klucz</span>
        </button>
      </div>

      {/* Info box */}
      <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
          <div className="text-sm text-purple-900">
            <p className="font-medium mb-1">Jak używać kluczy API?</p>
            <ol className="list-decimal list-inside space-y-1 text-purple-800">
              <li>Wygeneruj nowy klucz poniżej</li>
              <li>Skopiuj klucz <strong>(wyświetla się tylko raz!)</strong></li>
              <li>W n8n zainstaluj node <code className="bg-white px-1 rounded">n8n-nodes-aiagenthub</code> i wklej klucz w credentials</li>
              <li>Dla Claude Code / Claude Desktop / Cursor skopiuj gotową komendę <code className="bg-white px-1 rounded">claude mcp add</code>, którą pokażemy zaraz po wygenerowaniu klucza</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Tokens list */}
      {tokens.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Nie masz jeszcze żadnych kluczy API</p>
          <p className="text-sm">Wygeneruj klucz, aby połączyć n8n z panelem</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tokens.map((token) => (
            <div
              key={token.id}
              className={`border rounded-lg p-4 transition-colors ${
                token.is_active
                  ? 'border-gray-200 bg-white'
                  : 'border-red-200 bg-red-50 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-gray-900 truncate">{token.name}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      token.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {token.is_active ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                    <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                      {token.token_prefix}••••••••
                    </code>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span>{formatDate(token.created_at)}</span>
                    </span>
                    {token.last_used_at && (
                      <span className="text-gray-400 whitespace-nowrap">
                        Ostatnio: {formatDate(token.last_used_at)}
                      </span>
                    )}
                    {token.expires_at && (
                      <span className={`flex items-center gap-1 whitespace-nowrap ${
                        new Date(token.expires_at) < new Date() ? 'text-red-500' : 'text-gray-400'
                      }`}>
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        <span>Wygasa: {formatDate(token.expires_at)}</span>
                      </span>
                    )}
                  </div>
                  {token.scopes?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {token.scopes.map((scope) => (
                        <code key={scope} className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-xs">
                          {scope}
                        </code>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggleActive(token.id, token.is_active)}
                    className={`p-2 rounded-lg transition-colors ${
                      token.is_active
                        ? 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                        : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                    }`}
                    title={token.is_active ? 'Dezaktywuj' : 'Aktywuj'}
                  >
                    {token.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmToken(token)}
                    disabled={deletingId === token.id}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Usuń klucz"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal — portalled to <body>. Rendered in place it sat in this
          section's stacking context, and later siblings (the calendar) painted
          over it at the same z-index. */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4" onClick={(e) => {
          if (e.target === e.currentTarget && !createdToken) setShowCreateModal(false);
        }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {!createdToken ? (
              <>
                <h3 className="text-lg font-semibold mb-4">Wygeneruj nowy klucz API</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nazwa klucza
                  </label>
                  <input
                    type="text"
                    value={newTokenName}
                    onChange={(e) => setNewTokenName(e.target.value)}
                    placeholder="np. Mój workflow n8n"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreate();
                    }}
                  />
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Uprawnienia
                    </label>
                    <span className="text-xs text-gray-400">
                      {selectedScopes.length} z {AVAILABLE_SCOPES.length}
                    </span>
                  </div>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label
                        key={scope.value}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(scope.value)}
                          onChange={() => toggleScope(scope.value)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{scope.label}</span>
                            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-gray-600">{scope.value}</code>
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">{scope.hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={creating || !newTokenName.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
                  >
                    {creating ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <Key className="w-4 h-4" />
                    )}
                    <span>{creating ? 'Generowanie...' : 'Wygeneruj'}</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 mb-4">
                  <Check className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-900">Klucz wygenerowany!</h3>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-yellow-900">
                      <strong>Skopiuj ten klucz teraz!</strong> Nie będzie możliwości ponownego wyświetlenia go.
                    </p>
                  </div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <code className="text-green-400 text-sm break-all font-mono">
                      {createdToken}
                    </code>
                    <button
                      onClick={() => copyToClipboard(createdToken, 'token')}
                      className="ml-3 flex-shrink-0 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Kopiuj klucz"
                    >
                      {copiedKey === 'token' ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {createdScopes.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 mb-1.5">Nadane uprawnienia</p>
                    <div className="flex flex-wrap gap-1.5">
                      {createdScopes.map((scope) => (
                        <code key={scope} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                          {scope}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                {/* The key is shown exactly once, so this is the only moment the
                    command can be assembled with the token already in it. */}
                {createdScopes.includes('mcp:use') && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Podłącz do Claude Code / Claude Desktop / Cursor
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      Wklej tę komendę do terminala. Zawiera już Twój klucz — potem nie da się jej odtworzyć.
                    </p>
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-3">
                        <code className="text-blue-300 text-xs break-all font-mono whitespace-pre-wrap">
                          {mcpAddCommand(createdToken)}
                        </code>
                        <button
                          onClick={() => copyToClipboard(mcpAddCommand(createdToken), 'mcp')}
                          className="flex-shrink-0 p-2 text-gray-400 hover:text-white transition-colors"
                          title="Kopiuj komendę"
                        >
                          {copiedKey === 'mcp' ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreatedToken(null);
                    setCreatedScopes([]);
                  }}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Zamknij
                </button>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmToken && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4" onClick={(e) => {
          if (e.target === e.currentTarget) setDeleteConfirmToken(null);
        }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Usunąć klucz API?</h3>
            </div>
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-900">
                  <p className="font-medium mb-1">Ta operacja jest nieodwracalna!</p>
                  <p>Wszystkie integracje korzystające z klucza <strong>"{deleteConfirmToken.name}"</strong> (<code className="bg-red-100 px-1 rounded text-xs">{deleteConfirmToken.token_prefix}••••</code>) natychmiast przestaną działać.</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmToken(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmToken.id)}
                disabled={deletingId === deleteConfirmToken.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              >
                {deletingId === deleteConfirmToken.id ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                <span>{deletingId === deleteConfirmToken.id ? 'Usuwanie...' : 'Usuń klucz'}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
